import { useCallback, useEffect, useState } from 'react';
import { CircleDot } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

import usePageMetadata from '../../hooks/usePageMetadata';
import { getFit } from '../../services/toleranceService';
import { parseFitInputs } from './fitDisplay';
import FitResult from './FitResult';
import RecommendedFits from './RecommendedFits';
import './FitsPage.css';
import { useUnits } from '../../units/UnitContext';
import { displayUnitRange, unitLabel } from '../../units/unitConversions';
import { useUnitText } from '../../units/useUnitText';

const getInitialState = (searchParams) => {
  const queryDiameter = searchParams.get('diameter') || '25';
  const parsed = parseFitInputs({
    diameter: queryDiameter,
    hole: searchParams.get('hole') || '',
    shaft: searchParams.get('shaft') || '',
  });
  const numericDiameter = Number(queryDiameter);
  return {
    diameter: Number.isFinite(numericDiameter) && numericDiameter >= 1 && numericDiameter <= 500 ? queryDiameter : '25',
    selectedFit: parsed.status === 'valid'
      ? { designation: `${parsed.hole}/${parsed.shaft}`, hole: parsed.hole, shaft: parsed.shaft }
      : null,
  };
};

const RecommendedFitsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { unitSystem } = useUnits();
  const [initialState] = useState(() => getInitialState(searchParams));
  const [diameter, setDiameter, canonicalDiameter] = useUnitText(initialState.diameter, 'mm', unitSystem);
  const [selectedFit, setSelectedFit] = useState(initialState.selectedFit);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  usePageMetadata(
    'Recommended ISO 286 Fits | ASiJoLab',
    'Browse curated commonly used ISO 286 fits by basis system and fit category, then evaluate one at a chosen diameter.'
  );

  const selectFit = useCallback((fit) => {
    if (fit) setSelectedFit(fit);
  }, []);

  useEffect(() => {
    if (!selectedFit) return;
    const value = canonicalDiameter(diameter);
    if (!Number.isFinite(value) || value < 1 || value > 500) return;
    const nextSearch = new URLSearchParams({
      diameter: String(value), hole: selectedFit.hole, shaft: selectedFit.shaft,
    });
    if (searchParams.toString() !== nextSearch.toString()) {
      setSearchParams(nextSearch, { replace: true });
    }
  }, [canonicalDiameter, diameter, searchParams, selectedFit, setSearchParams, unitSystem]);

  useEffect(() => {
    if (!selectedFit) return undefined;
    const value = canonicalDiameter(diameter);
    if (!Number.isFinite(value) || value < 1 || value > 500) {
      setMessage(`Enter a nominal diameter from ${displayUnitRange(1, 500, 'mm', unitSystem)}.`);
      setResult(null);
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setResult(null);
    setMessage('');

    getFit({
      system: 'ISO286', diameter: value, hole: selectedFit.hole, shaft: selectedFit.shaft,
    }, { signal: controller.signal })
      .then((loaded) => { if (active) setResult(loaded); })
      .catch((error) => {
        if (!active || error?.code === 'ERR_CANCELED') return;
        if (error?.response?.status === 404) setMessage('Fit not available for this diameter.');
        else if (error?.response?.status === 400) setMessage(error?.response?.data?.message || 'Check the fit designations.');
        else setMessage('The fit could not be loaded. Please try again.');
      })
      .finally(() => { if (active) setLoading(false); });

    return () => {
      active = false;
      controller.abort();
    };
  }, [canonicalDiameter, diameter, selectedFit, unitSystem]);

  const calculatorUrl = result
    ? `/tolerances/fits?${new URLSearchParams({
      diameter: String(canonicalDiameter(diameter)), hole: result.hole.designation, shaft: result.shaft.designation,
    })}`
    : '';

  const selectedContent = selectedFit && (
    <section className="recommendedFitResult" aria-labelledby="selected-fit-title">
      <header className="fitContentIntro">
        <p>Current selection</p>
        <h2 id="selected-fit-title">Selected fit</h2>
      </header>
      <div className="recommendedFitCalculationStatus" aria-live="polite">
        {loading && <p><CircleDot size={17} aria-hidden="true" /> Calculating {selectedFit.designation}…</p>}
        {!loading && message && <p className="fitMessage fitMessage--error" role="alert">{message}</p>}
      </div>
      {result && <FitResult result={result} />}
      {result && <Link className="recommendedFitCalculatorLink" to={calculatorUrl}>Open in Fit Calculator</Link>}
    </section>
  );

  return (
    <main className="fitsPage">
      <header className="fitsPageHeader">
        <p>Tolerances · curated fits</p>
        <h1>Recommended ISO 286 Fits</h1>
        <span>Browse commonly used hole-and-shaft combinations and inspect each fit at your chosen diameter.</span>
      </header>
      <section className="recommendedFitsDiameter" aria-labelledby="recommended-diameter-title">
        <div>
          <h2 id="recommended-diameter-title">Evaluation diameter</h2>
          <p>The curated list does not depend on diameter. This value is used to calculate the selected fit.</p>
        </div>
        <label htmlFor="recommended-diameter">
          <span>Nominal diameter</span>
          <div className="fitInputWithUnit">
            <input id="recommended-diameter" inputMode="decimal" value={diameter} onChange={(event) => { setDiameter(event.target.value); setMessage(''); }} />
            <span>{unitLabel('mm', unitSystem)}</span>
          </div>
        </label>
      </section>
      <RecommendedFits
        onSelect={selectFit}
        preserveInitialSelection={Boolean(initialState.selectedFit)}
        selectedDesignation={selectedFit?.designation || ''}
        selectedContent={selectedContent}
      />
    </main>
  );
};

export default RecommendedFitsPage;
